import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {Observable} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {Operand} from '@em/shared/mapping-engine/domain-models';

@Component({
  selector: 'em-combine-attributes',
  templateUrl: './combine-attributes.component.html',
  styleUrls: ['./combine-attributes.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmCombineAttributesComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class EmCombineAttributesComponent
  implements ControlValueAccessor, OnInit
{
  @Input() set availableAttributes(value: string[] | undefined | null) {
    if (value) {
      this.availableOperands = value.map(
        (attribute) =>
          ({
            value: attribute,
            type: 'attribute',
          }) as Operand,
      );
    } else {
      this.availableOperands = value;
    }
  }
  // To show custom attribute in different group/icon
  @Input() customAttributes: string[] | undefined | null;
  @ViewChild('operandInput') operandInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  readonly separatorKeysCodes: number[] = [ENTER];
  readonly inputControl = new UntypedFormControl('');
  filteredProperties$: Observable<Operand[]> | undefined;
  filteredPredefinedProperties$: Observable<Operand[]> | undefined;
  filteredCustomProperties$: Observable<Operand[] | null> | undefined;
  selectedOperands: Operand[] = [];
  availableOperands: Operand[] | null | undefined = [];

  private _onChangeCallback?: (val: Operand[]) => void;

  constructor() {}

  ngOnInit(): void {
    this.filteredProperties$ = this.inputControl.valueChanges.pipe(
      startWith(null),
      filter((value) => !value || typeof value === 'string'),
      map((value) => this._getFilteredProperties(value)),
    );

    this.filteredPredefinedProperties$ = this.filteredProperties$.pipe(
      map((all) =>
        all.filter(
          (attr) =>
            attr.value &&
            (!this.customAttributes ||
              this.customAttributes?.indexOf(attr.value.toString()) === -1),
        ),
      ),
    );

    this.filteredCustomProperties$ = this.filteredProperties$.pipe(
      map((all) =>
        all.filter(
          (attr) =>
            attr.value &&
            this.customAttributes &&
            this.customAttributes?.indexOf(attr.value.toString()) > -1,
        ),
      ),
      map((custom) => (custom.length ? custom : null)),
    );
  }

  //#region ControlValueAccessor implementation
  writeValue(operands: Operand[]): void {
    this.selectedOperands = [...(operands || [])];
  }

  registerOnChange(fn: (val: Operand[]) => void): void {
    this._onChangeCallback = fn;
  }

  registerOnTouched(): void {
    // throw new Error('Method not implemented.');
  }
  //#endregion

  add(event: MatChipInputEvent): void {
    if (!event.value) {
      return;
    }

    let selected = this.availableOperands?.find(
      (prop) => prop.value === event.value,
    );

    if (!selected) {
      selected = {
        value: event.value,
        type: 'static_value',
      };
    }

    if (selected) {
      this.selectedOperands.push(selected);
    }

    // Clear the input value
    event.chipInput?.clear();

    this.inputControl.setValue(null);
    this._emitChanges();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedOperands.push(event.option.value as Operand);
    if (this.operandInput) {
      this.operandInput.nativeElement.value = '';
    }

    this.inputControl.setValue(null);
    this._emitChanges();
  }

  remove(operand: Operand): void {
    const index = this.selectedOperands.indexOf(operand);

    if (index >= 0) {
      this.selectedOperands.splice(index, 1);
    }

    this._emitChanges();
  }

  isCustom(attr: string | number | boolean | undefined): boolean {
    return !!attr && !!this.customAttributes?.includes(attr.toString());
  }

  private _getFilteredProperties(value: string): Operand[] {
    if (!value) {
      return this.availableOperands || [];
    }
    const filteredProperties =
      this.availableOperands?.filter((prop) =>
        prop.value
          ?.toString()
          .toLowerCase()
          .includes(value.toLocaleLowerCase()),
      ) ||
      this.availableOperands ||
      [];

    // Add user input as static value at the top of the list
    return [{type: 'static_value', value}, ...filteredProperties];
  }

  private _emitChanges() {
    if (this._onChangeCallback) {
      this._onChangeCallback([...this.selectedOperands]);
    }
  }
}
